<template>
  <!-- Figma 1:13536 › ອັບໂຫລດສລິບການໂອນເງິນ (711:3116). The dialog owns the title. -->
  <div class="slip-modal">
    <div class="info-box">
      <div class="info-row">
        <span>ຮ້ານຄ້າ</span>
        <b>{{ invoice.shop ? invoice.shop.name : '—' }}</b>
      </div>
      <div class="info-row">
        <span>ຈຳນວນເງິນ</span>
        <b class="is-amount">{{ formatKip(invoice.total_price) }}</b>
      </div>
      <!-- Not in the design, but it is where the money goes: the account comes
           back only with the claim, so only the operator holding the row sees it. -->
      <div class="info-row">
        <span>ບັນຊີ</span>
        <b>
          {{ bank.bank_name || '—' }}<template v-if="bank.account_name"> · {{ bank.account_name }}</template>
          <span class="account-no">{{ bank.account_number || '—' }}</span>
        </b>
      </div>
    </div>

    <div class="fld">
      <label class="fld-label">ຮູບສລິບເງິນໂອນ <i class="req">*</i></label>
      <input ref="file" class="is-hidden-input" type="file" accept=".jpg,.jpeg,.png" @change="onFile">
      <div class="dropzone" v-if="!slip" :class="{'is-over': dragOver}"
           @click="openFilePicker" @dragover.prevent="dragOver = true"
           @dragleave.prevent="dragOver = false" @drop.prevent="onDrop">
        <i class="fal fa-cloud-upload dz-icon"></i>
        <span class="dz-text is-plain">ລາກຮູບໃສ່ນີ້ ຫຼື <u>ເລືອກໄຟລ໌</u></span>
        <span class="dz-hint">PNG, JPG · ສູງສຸດ 10MB</span>
      </div>
      <div class="file-picked" v-else>
        <img class="file-thumb" v-if="slipPreview" :src="slipPreview" alt="">
        <span class="picked-name">{{ slip.name }}</span>
        <span class="picked-size">{{ fileSize(slip.size) }}</span>
        <button class="chosen-x" type="button" @click="removeFile" aria-label="ລຶບ">
          <i class="fal fa-times"></i>
        </button>
      </div>
    </div>

    <div class="fld">
      <label class="fld-label">ຈຳນວນເງິນ <i class="req">*</i></label>
      <!-- Locked to the invoice total: the server accepts that amount and no
           other (one payout is ONE full transfer, never instalments), so an
           editable field only offered ways to be refused. -->
      <div class="suffixed">
        <input class="fld-input is-locked" type="text" readonly tabindex="-1" aria-readonly="true"
               :value="groupedTotal">
        <span class="suffix">ກີບ</span>
      </div>
      <p class="fld-hint">ໂອນເຕັມຈຳນວນຂອງລາຍການນີ້ເທົ່ານັ້ນ</p>
    </div>

    <div class="fld">
      <label class="fld-label">ວັນທີໂອນ <i class="req">*</i></label>
      <input class="fld-input" type="date" v-model="transferredAt">
    </div>

    <p class="form-error" v-if="error">{{ error }}</p>

    <div class="modal-actions is-split">
      <button class="cod-btn is-ghost" type="button" @click="$emit('close')">ຍົກເລີກ</button>
      <button class="cod-btn" type="button" :disabled="saving || !canSubmit" @click="submit">
        {{ saving ? 'ກຳລັງອັບໂຫລດ...' : 'ອັບໂຫລດສລິບ' }}
      </button>
    </div>
  </div>
</template>

<script>
import {errorMessage, formatKip, uploadTransferSlip} from '@/services/codAdvanceService';

const MAX_SLIP_BYTES = 10 * 1024 * 1024;

export default {
  name: 'SlipUploadModal',
  props: {
    invoice: {type: Object, required: true},
    bank: {type: Object, default: () => ({})},
  },
  data() {
    return {
      transferredAt: '',
      slip: null,
      slipPreview: null,
      dragOver: false,
      error: null,
      saving: false,
    };
  },
  computed: {
    // A decimal column: the API sends "3600000.00".
    total() {
      return Number(this.invoice.total_price) || 0;
    },
    groupedTotal() {
      return this.total.toLocaleString('en-US');
    },
    canSubmit() {
      return !!this.slip && this.total > 0 && !!this.transferredAt;
    },
  },
  beforeDestroy() {
    this.revokePreview();
  },
  methods: {
    formatKip,
    fileSize(bytes) {
      const kb = (Number(bytes) || 0) / 1024;
      return kb >= 1024 ? (kb / 1024).toFixed(1) + ' MB' : Math.round(kb) + ' KB';
    },
    openFilePicker() {
      this.$refs.file.click();
    },
    onFile(event) {
      this.acceptFile(event.target.files[0] || null);
    },
    onDrop(event) {
      this.dragOver = false;
      const file = event.dataTransfer && event.dataTransfer.files
        ? event.dataTransfer.files[0]
        : null;
      this.acceptFile(file);
    },
    acceptFile(file) {
      if (!file) return;
      if (!/\.(jpe?g|png)$/i.test(file.name)) {
        this.error = 'ຮັບສະເພາະ PNG ຫຼື JPG';
        return;
      }
      if (file.size > MAX_SLIP_BYTES) {
        this.error = 'ຟາຍໃຫຍ່ເກີນ 10 MB';
        return;
      }
      this.error = null;
      this.revokePreview();
      this.slip = file;
      this.slipPreview = URL.createObjectURL(file);
    },
    removeFile() {
      this.revokePreview();
      this.slip = null;
      if (this.$refs.file) {
        this.$refs.file.value = '';
      }
    },
    revokePreview() {
      if (this.slipPreview) {
        URL.revokeObjectURL(this.slipPreview);
        this.slipPreview = null;
      }
    },
    async submit() {
      if (this.saving) return;
      this.error = null;
      if (!this.canSubmit) {
        this.error = 'ກະລຸນາປ້ອນຂໍ້ມູນໃຫ້ຄົບ';
        return;
      }
      this.saving = true;
      try {
        const data = new FormData();
        data.append('slip', this.slip);
        // Still sent: the server re-checks it against the invoice under its
        // lock, so a total that changed since this list loaded is refused.
        data.append('amount', this.total);
        data.append('transferred_at', this.transferredAt);
        this.$emit('done', await uploadTransferSlip(this.invoice.id, data));
      } catch (e) {
        this.error = errorMessage(e, 'ອັບໂຫລດບໍ່ສຳເລັດ');
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import './cod-advance-ui';

.slip-modal {
  text-align: left;
  color: $cod-ink;
}

.account-no {
  display: block;
  font-variant-numeric: tabular-nums;
}

.dropzone .dz-text.is-plain {
  color: $cod-ink;

  u {
    color: $cod-blue;
  }
}

.fld-input.is-locked {
  background: #f7f8fa;
  color: $cod-ink;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  cursor: default;

  &:focus {
    border-color: $cod-line;
    box-shadow: none;
  }
}
</style>
